/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, ReactNode } from 'react';
import { FormikHelpers, FormikValues } from 'formik';
import { InitialValue } from '../../../types';
import { DiscardProps, FormikFormProps, ModalProps } from '../FormikForm/types';
import { FormStepStatusDto } from '../../../api/web-api-client';

export interface WizardFormStepValues<T extends FormikValues> {
    stepValues: InitialValue<T>;
}

export type WizardRoutedStepProps<T extends FormikValues> =
  Omit<WizardStepProps<T>, 'children'>
  & Omit<WizardFormProps, 'children' | 'loadFormStepStatuses'>
  & {
      allSteps: React.ReactElement<WizardStepProps<FormikValues>, string | React.JSXElementConstructor<any>>[];
      currentStepIndex: number;
      url: string;
  };

export enum ErrorType {
    Load,
    Update,
}

export type WizardStepProps<T extends FormikValues> =
Omit<FormikFormProps<T>,
'isLoading' |
'promptPath' |
'onSubmit' |
'children' |
'initialValues' |
'canSaveDraft' |
'showBanner' |
'onSaveAndExit'>
& {
    title: string;
    children?: ReactNode;
    location: string;
    initialValues: InitialValue<T>;
    isSummaryPage?: boolean;
    getRedirectionLocationOnError?: (errorCode: number, errorType: ErrorType) => string | undefined;
    discard?: DiscardProps | undefined;
    onSaveAndExit?: (
        values: T,
        isDirty: boolean,
        formikHelpers: FormikHelpers<T>,
        abortSignal?: AbortSignal) => void | Promise<any>;
    onSaveAndNext?: (
        values: T,
        isDirty: boolean,
        formikHelpers: FormikHelpers<T>,
        abortSignal?: AbortSignal) => void | Promise<any>;
    stepStatuses: FormStepStatusDto[];
    loadStepValues: (abortSignal?: AbortSignal) => WizardFormStepValues<T> | Promise<WizardFormStepValues<T>>;
};

export interface SubmitFormResult {
    baseUrl?: string;
}

export interface WizardFormProps {
    children?: ReactElement<FormikValues> | Array<ReactElement<FormikValues>>;
    previousButtonTitle?: string;
    nextButtonTitle?: string;
    lastStepNextButtonTitle?: string;
    locationAfterExit?: string;
    locationOnCompletion: string;
    canSaveDraft?: boolean | undefined;
    showSaveAndNextButton?: boolean;
    showGoToDashboardButton?: boolean;
    showBanner?: boolean | undefined;
    confirmationOnSubmission?: ModalProps | undefined;
    getRedirectionLocationOnError?: (errorCode: number, errorType: ErrorType) => string | undefined;
}

export interface PreviousStepButtonProps {
    steps: (React.ReactChild | React.ReactFragment | React.ReactPortal)[];
    currentStepIndex: number;
    title?: string;
    url: string;
    className?: string;
}

export interface NextStepButtonProps {
    steps: (React.ReactChild | React.ReactFragment | React.ReactPortal)[];
    currentStepIndex: number;
    title?: string;
    finalStepTitle?: string;
    finalStepConfirmation?: ModalProps | undefined;
    className?: string;
}
